/**
 * BHADKO OTT - Main Application Orchestrator
 * Connects Discovery Grid, Live Video Player, Navigation, Admin Console, and TV Mode.
 */

import '../styles/variables.css';
import '../styles/base.css';
import '../styles/navbar.css';
import '../styles/channel-grid.css';
import '../styles/player.css';
import '../styles/admin.css';
import '../styles/tv-mode.css';
import '../styles/footer.css';

import { StorageManager } from './storage.js';
import { TRANSLATIONS } from '../data/i18n.js';
import { CATEGORIES } from '../data/channels.js';
import { ICONS, getChannelLogoSvg, getChannelPosterSvg } from './assets.js';
import { PlayerEngine } from './player.js';
import { TVNavigator } from './tv-navigation.js';
import { AdminController } from './admin.js';

class BhadkoApp {
  constructor() {
    this.currentView = 'channels'; // 'channels' | 'player' | 'favorites' | 'categories' | 'admin'
    this.currentLang = StorageManager.getLanguage() || 'gu';
    this.channels = StorageManager.getChannels();
    this.selectedChannel = this.channels[0] || null;
    
    // Filters & Search
    this.searchQuery = '';
    this.selectedCategory = 'all';
    this.selectedLanguageFilter = 'all';
    this.selectedSort = 'latest';
    this.channelsDisplayLimit = 6;
    
    // Player State
    this.playerEngine = null;
    this.activePlayerTab = 'about';
    this.availableQualities = [];
    
    // Admin & TV Mode
    this.tvNavigator = null;
    this.adminController = null;
    this.toastTimer = null;

    this.init();
  }

  init() {
    this.renderShell();
    this.initTVNavigation();
    this.initAdmin();
    this.renderCurrentView();
    this.bindGlobalEvents();
  }

  get t() {
    return TRANSLATIONS[this.currentLang] || TRANSLATIONS.gu;
  }

  renderShell() {
    const app = document.getElementById('app');
    app.innerHTML = `
      <!-- Global Top Header -->
      <header class="app-header" id="appHeader">
        <div class="brand-container" id="brandLogoBtn" title="BHADKO OTT">
          ${ICONS.logo}
          <div class="brand-meta">
            <span class="brand-title">BHADKO <span class="brand-accent">OTT</span></span>
            <span class="brand-tagline">BHADKO DIGITAL TV MEDIA</span>
          </div>
        </div>

        <div class="header-search-container">
          <div class="search-input-wrapper">
            <span class="search-icon">${ICONS.search}</span>
            <input 
              type="text" 
              id="globalSearchInput" 
              class="global-search-input" 
              placeholder="${this.t.searchPlaceholder}" 
              value="${this.searchQuery}" 
            />
            <button class="search-clear-btn ${this.searchQuery ? '' : 'hidden'}" id="searchClearBtn" title="Clear search">
              ${ICONS.close}
            </button>
          </div>
        </div>

        <nav class="header-nav" id="desktopNav">
          <button class="nav-link-btn ${this.currentView === 'channels' ? 'active' : ''}" data-nav="channels">
            ${this.t.navHome}
          </button>
          <button class="nav-link-btn ${this.currentView === 'player' ? 'active' : ''}" data-nav="player">
            ${this.t.navLiveTV}
          </button>
          <button class="nav-link-btn ${this.currentView === 'favorites' ? 'active' : ''}" data-nav="favorites">
            ${this.t.navFavorites}
            <span class="badge-count" id="navFavCount">${StorageManager.getFavorites().length}</span>
          </button>
          <button class="nav-link-btn ${this.currentView === 'categories' ? 'active' : ''}" data-nav="categories">
            ${this.t.navCategories}
          </button>
        </nav>

        <div class="header-actions">
          <!-- Language Selector Dropdown -->
          <div class="lang-dropdown-wrapper">
            <button class="lang-select-btn" id="langSelectBtn">
              <span>🌐 ${this.getLangLabel(this.currentLang)}</span>
              ${ICONS.chevronDown}
            </button>
            <div class="lang-menu-dropdown hidden" id="langMenuDropdown">
              <button class="lang-option-btn ${this.currentLang === 'gu' ? 'selected' : ''}" data-lang="gu">
                <span>ગુજરાતી</span>
                ${this.currentLang === 'gu' ? ICONS.check : ''}
              </button>
              <button class="lang-option-btn ${this.currentLang === 'hi' ? 'selected' : ''}" data-lang="hi">
                <span>हिन्दी</span>
                ${this.currentLang === 'hi' ? ICONS.check : ''}
              </button>
              <button class="lang-option-btn ${this.currentLang === 'en' ? 'selected' : ''}" data-lang="en">
                <span>English</span>
                ${this.currentLang === 'en' ? ICONS.check : ''}
              </button>
            </div>
          </div>

          <!-- TV Remote Mode Toggle -->
          <button class="tv-mode-btn" id="btnToggleTvMode" title="Smart TV Remote Navigation Mode">
            ${ICONS.tv}
            <span>${this.t.navTvMode}</span>
          </button>

          <!-- Admin Console Button -->
          <button class="btn-icon admin-nav-btn ${this.currentView === 'admin' ? 'active' : ''}" id="btnOpenAdmin" title="${this.t.navAdmin}">
            ${ICONS.settings}
          </button>
        </div>
      </header>

      <!-- Main Dynamic Content Container -->
      <main class="main-content-area" id="mainContent"></main>

      <!-- Mobile Bottom Navigation -->
      <nav class="mobile-bottom-nav">
        <button class="mobile-nav-item ${this.currentView === 'channels' ? 'active' : ''}" data-nav="channels">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
          <span>${this.t.navHome}</span>
        </button>
        <button class="mobile-nav-item ${this.currentView === 'player' ? 'active' : ''}" data-nav="player">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><rect x="2" y="7" width="20" height="15" rx="2"></rect><polyline points="17 2 12 7 7 2" stroke="currentColor" stroke-width="2"></polyline></svg>
          <span>${this.t.navLiveTV}</span>
        </button>
        <button class="mobile-nav-item" id="mobileSearchTab">
          ${ICONS.search}
          <span>શોધો</span>
        </button>
        <button class="mobile-nav-item ${this.currentView === 'favorites' ? 'active' : ''}" data-nav="favorites">
          ${ICONS.starFilled}
          <span>${this.t.navFavorites}</span>
        </button>
        <button class="mobile-nav-item ${this.currentView === 'admin' ? 'active' : ''}" data-nav="admin">
          ${ICONS.settings}
          <span>મેનૂ</span>
        </button>
      </nav>

      <!-- App Footer -->
      <footer class="app-footer" id="appFooter">
        <div class="footer-inner-container">
          <div class="footer-grid">
            <div class="footer-brand-column">
              <div class="footer-brand-header">
                ${ICONS.logo}
                <span class="footer-brand-title">BHADKO OTT</span>
              </div>
              <p class="footer-description">${this.t.footerDesc}</p>
              <div class="footer-social-row">
                <a href="#facebook" class="social-circle-link" title="Facebook">f</a>
                <a href="#help" class="social-circle-link" title="Help">?</a>
                <a href="#instagram" class="social-circle-link" title="Instagram">ig</a>
                <a href="#youtube" class="social-circle-link" title="YouTube">▶</a>
              </div>
            </div>

            <div class="footer-links-column">
              <h4>${this.t.footerExplore}</h4>
              <ul class="footer-links-list">
                <li><a href="#" data-nav="player">${this.t.navLiveTV}</a></li>
                <li><a href="#" data-nav="channels">${this.t.navAllChannels}</a></li>
                <li><a href="#" data-nav="categories">${this.t.navCategories}</a></li>
                <li><a href="#" data-nav="favorites">${this.t.navFavorites}</a></li>
              </ul>
            </div>

            <div class="footer-links-column">
              <h4>${this.t.footerSupport}</h4>
              <ul class="footer-links-list">
                <li><a href="#help">${this.t.helpCenter}</a></li>
                <li><a href="#terms">${this.t.terms}</a></li>
                <li><a href="#privacy">${this.t.privacy}</a></li>
                <li><a href="#contact">${this.t.contactUs}</a></li>
              </ul>
            </div>
          </div>

          <div class="footer-bottom-bar">
            <p class="footer-copyright">${this.t.copyright}</p>
          </div>
        </div>
      </footer>

      <!-- Floating TV Remote Guide Banner (Visible when TV Mode is active) -->
      <div class="tv-remote-guide-banner hidden" id="tvRemoteGuide">
        <div class="tv-guide-item">
          <span class="tv-key-badge">▲ ▼ ◄ ►</span>
          <span>નેવિગેટ કરો</span>
        </div>
        <div class="tv-guide-item">
          <span class="tv-key-badge">Enter / OK</span>
          <span>પસંદ કરો</span>
        </div>
        <div class="tv-guide-item">
          <span class="tv-key-badge">Esc</span>
          <span>પાછા જાઓ</span>
        </div>
      </div>

      <!-- Toast Container -->
      <div class="toast-container" id="toastContainer"></div>

      <!-- Modal Injection Target -->
      <div id="modalContainer"></div>
    `;
  }

  getLangLabel(lang) {
    if (lang === 'hi') return 'हिन्दी';
    if (lang === 'en') return 'English';
    return 'ગુજરાતી';
  }

  initTVNavigation() {
    this.tvNavigator = new TVNavigator({
      onModeChange: (isActive) => {
        const tvGuide = document.getElementById('tvRemoteGuide');
        const tvBtn = document.getElementById('btnToggleTvMode');
        if (isActive) {
          tvGuide?.classList.remove('hidden');
          tvBtn?.classList.add('active');
          this.showToast(this.t.tvModeActive);
        } else {
          tvGuide?.classList.add('hidden');
          tvBtn?.classList.remove('active');
        }
      }
    });

    if (StorageManager.getTVMode()) {
      this.tvNavigator.enable();
    }
  }

  initAdmin() {
    this.adminController = new AdminController({
      container: document.getElementById('modalContainer'),
      onCatalogUpdated: () => {
        this.channels = StorageManager.getChannels();
        if (this.currentView === 'admin') {
          this.renderAdminView();
        } else {
          this.renderCurrentView();
        }
      },
      onPreviewStream: (channel) => {
        this.selectedChannel = channel;
        this.switchView('player');
      }
    });
  }

  switchView(viewName) {
    // If leaving player, pause video session
    if (this.currentView === 'player' && viewName !== 'player') {
      if (this.playerEngine) {
        this.playerEngine.releaseSession();
      }
    }

    this.currentView = viewName;

    // Update active nav links
    document.querySelectorAll('[data-nav]').forEach(el => {
      if (el.getAttribute('data-nav') === viewName) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });

    // Update favorite count badge
    const favCount = document.getElementById('navFavCount');
    if (favCount) {
      favCount.textContent = StorageManager.getFavorites().length;
    }

    this.renderCurrentView();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  renderCurrentView() {
    const main = document.getElementById('mainContent');
    if (!main) return;

    switch (this.currentView) {
      case 'channels':
        this.renderChannelsView(main);
        break;
      case 'player':
        this.renderPlayerView(main);
        break;
      case 'favorites':
        this.renderFavoritesView(main);
        break;
      case 'categories':
        this.renderCategoriesView(main);
        break;
      case 'admin':
        this.renderAdminView(main);
        break;
      default:
        this.renderChannelsView(main);
    }

    // Refresh TV navigation focus pool
    if (this.tvNavigator && this.tvNavigator.isEnabled) {
      setTimeout(() => this.tvNavigator.focusFirstAvailable(), 100);
    }
  }

  // =========================================================
  // VIEW: ALL CHANNELS DISCOVERY (PAGE 1 REFERENCE)
  // =========================================================
  renderChannelsView(container) {
    const filteredChannels = this.getFilteredChannels();
    const visibleChannels = filteredChannels.slice(0, this.channelsDisplayLimit);
    const hasMore = filteredChannels.length > this.channelsDisplayLimit;

    container.innerHTML = `
      <section class="discovery-view">
        <div class="discovery-header">
          <div class="discovery-title-row">
            <div class="discovery-title-group">
              <h1>${this.t.allChannelsTitle}</h1>
              <p>${this.t.allChannelsSubtitle}</p>
            </div>

            <div class="secondary-filters-group">
              <select class="select-filter-custom" id="filterLanguageSelect">
                <option value="all" ${this.selectedLanguageFilter === 'all' ? 'selected' : ''}>${this.t.allLanguages}</option>
                <option value="Gujarati" ${this.selectedLanguageFilter === 'Gujarati' ? 'selected' : ''}>Gujarati (ગુજરાતી)</option>
                <option value="Hindi" ${this.selectedLanguageFilter === 'Hindi' ? 'selected' : ''}>Hindi (हिन्दी)</option>
                <option value="English" ${this.selectedLanguageFilter === 'English' ? 'selected' : ''}>English</option>
              </select>

              <select class="select-filter-custom" id="filterSortSelect">
                <option value="latest" ${this.selectedSort === 'latest' ? 'selected' : ''}>${this.t.sortLatest}</option>
                <option value="popular" ${this.selectedSort === 'popular' ? 'selected' : ''}>${this.t.sortPopular}</option>
                <option value="az" ${this.selectedSort === 'az' ? 'selected' : ''}>${this.t.sortAlphabetical}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Category Filter Pills Bar -->
        <div class="category-pills-bar" id="categoryPillsBar">
          ${CATEGORIES.map(cat => {
            const label = this.currentLang === 'hi' ? cat.labelHi : (this.currentLang === 'en' ? cat.labelEn : cat.labelGu);
            const isActive = this.selectedCategory.toLowerCase() === cat.id.toLowerCase();
            return `
              <button class="category-pill ${isActive ? 'active' : ''}" data-category="${cat.id}">
                ${label}
              </button>
            `;
          }).join('')}
        </div>

        <!-- 3-Column Channels Grid -->
        <div class="channels-grid" id="channelsGrid">
          ${visibleChannels.length > 0 ? visibleChannels.map(channel => this.renderChannelCard(channel)).join('') : this.renderEmptyState()}
        </div>

        ${hasMore ? `
          <div class="load-more-container">
            <button class="load-more-btn" id="btnLoadMoreChannels">
              ${this.t.loadMore}
            </button>
          </div>
        ` : ''}
      </section>
    `;

    this.bindDiscoveryEvents();
  }

  renderChannelCard(channel) {
    const isFav = StorageManager.isFavorite(channel.id);
    const isLive = channel.status === 'LIVE';
    const isSoon = channel.status === 'STARTING_SOON';

    let statusPill = `<span class="status-pill status-offline">${this.t.offline}</span>`;
    if (isLive) {
      statusPill = `<span class="status-pill status-live"><span class="pulse-dot"></span> ${this.t.liveBadge}</span>`;
    } else if (isSoon) {
      statusPill = `<span class="status-pill status-soon">${this.t.startingSoon}</span>`;
    }

    return `
      <article class="channel-card" data-channel-id="${channel.id}" tabindex="0">
        <div class="card-media-wrapper">
          ${getChannelPosterSvg(channel)}
          <div class="card-badge-top-left">
            ${statusPill}
          </div>
          <button class="card-favorite-btn ${isFav ? 'is-fav' : ''}" data-action="toggle-fav" data-id="${channel.id}" title="${isFav ? this.t.removeFromFavorites : this.t.addToFavorites}">
            ${isFav ? ICONS.starFilled : ICONS.starOutline}
          </button>
        </div>

        <div class="card-body">
          <div class="card-info-header">
            <div class="card-logo-box">
              ${getChannelLogoSvg(channel)}
            </div>
            <div class="card-title-group">
              <h3 class="channel-title">${channel.name}</h3>
              <span class="channel-subtitle">${channel.gujaratiName || ''} • ${channel.category}</span>
            </div>
          </div>

          <p class="channel-description">${channel.description || ''}</p>

          <div class="card-action-bar">
            <button class="watch-live-btn" data-action="watch-live" data-id="${channel.id}">
              ${ICONS.play}
              <span>${this.t.watchLive}</span>
            </button>
          </div>
        </div>
      </article>
    `;
  }

  renderEmptyState() {
    return `
      <div class="empty-channels-state">
        <div class="empty-state-icon">
          ${ICONS.search}
        </div>
        <h3>${this.t.noChannelsFound}</h3>
        <p>${this.t.noChannelsSubtitle}</p>
        <button class="btn btn-secondary" id="btnResetFilters">${this.t.resetFilters}</button>
      </div>
    `;
  }

  getFilteredChannels() {
    let list = [...this.channels];

    // Search query filter
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      list = list.filter(c => 
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.gujaratiName && c.gujaratiName.toLowerCase().includes(q)) ||
        (c.category && c.category.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q)) ||
        (c.language && c.language.toLowerCase().includes(q))
      );
    }

    // Category filter
    if (this.selectedCategory !== 'all') {
      list = list.filter(c => c.category.toLowerCase() === this.selectedCategory.toLowerCase());
    }

    // Language filter
    if (this.selectedLanguageFilter !== 'all') {
      list = list.filter(c => c.language === this.selectedLanguageFilter);
    }

    // Sort order
    if (this.selectedSort === 'az') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (this.selectedSort === 'popular') {
      list.sort((a, b) => (parseFloat(b.viewers) || 0) - (parseFloat(a.viewers) || 0));
    } else {
      list.sort((a, b) => (a.displayOrder || 99) - (b.displayOrder || 99));
    }

    return list;
  }

  bindDiscoveryEvents() {
    // Category pills
    document.querySelectorAll('.category-pill').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.selectedCategory = e.currentTarget.getAttribute('data-category');
        this.renderChannelsView(document.getElementById('mainContent'));
      });
    });

    // Language dropdown
    const langSelect = document.getElementById('filterLanguageSelect');
    if (langSelect) {
      langSelect.addEventListener('change', (e) => {
        this.selectedLanguageFilter = e.target.value;
        this.renderChannelsView(document.getElementById('mainContent'));
      });
    }

    // Sort dropdown
    const sortSelect = document.getElementById('filterSortSelect');
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        this.selectedSort = e.target.value;
        this.renderChannelsView(document.getElementById('mainContent'));
      });
    }

    // Load More
    const loadMoreBtn = document.getElementById('btnLoadMoreChannels');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => {
        this.channelsDisplayLimit += 6;
        this.renderChannelsView(document.getElementById('mainContent'));
      });
    }

    // Reset Filters
    const resetBtn = document.getElementById('btnResetFilters');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.searchQuery = '';
        this.selectedCategory = 'all';
        this.selectedLanguageFilter = 'all';
        const globalSearch = document.getElementById('globalSearchInput');
        if (globalSearch) globalSearch.value = '';
        this.renderChannelsView(document.getElementById('mainContent'));
      });
    }

    // Card click & Watch Live button
    const grid = document.getElementById('channelsGrid');
    if (grid) {
      grid.addEventListener('click', (e) => {
        const favBtn = e.target.closest('[data-action="toggle-fav"]');
        if (favBtn) {
          e.stopPropagation();
          const channelId = favBtn.getAttribute('data-id');
          this.toggleFavorite(channelId);
          return;
        }

        const card = e.target.closest('.channel-card');
        if (card) {
          const channelId = card.getAttribute('data-channel-id');
          const channel = StorageManager.getChannelById(channelId);
          if (channel) {
            this.selectedChannel = channel;
            this.switchView('player');
          }
        }
      });
    }
  }

  // =========================================================
  // VIEW: LIVE TV PLAYER (PAGE 3 REFERENCE DESKTOP & PAGE 2 MOBILE)
  // =========================================================
  renderPlayerView(container) {
    if (!this.selectedChannel) {
      this.selectedChannel = this.channels[0];
    }

    const current = this.selectedChannel;
    const isFav = StorageManager.isFavorite(current.id);
    const isLive = current.status === 'LIVE';

    container.innerHTML = `
      <div class="player-page-wrapper">
        <!-- Searchable Channels Sidebar (Desktop Left) -->
        <aside class="player-sidebar">
          <div class="sidebar-header">
            <div class="sidebar-title-group">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
              <span>${this.t.channelSidebarTitle}</span>
            </div>
            <button class="sidebar-filter-btn" id="sidebarFilterBtn">
              ${ICONS.filter}
              <span>${this.t.filterBtn}</span>
            </button>
          </div>

          <div class="sidebar-search-box">
            <input 
              type="text" 
              class="sidebar-search-input" 
              id="sidebarSearchInput" 
              placeholder="${this.t.searchChannelsPlaceholder}" 
            />
          </div>

          <div class="sidebar-channels-list" id="sidebarChannelsList">
            ${this.renderSidebarItems()}
          </div>
        </aside>

        <!-- Main Video Player Stage (Right) -->
        <section class="player-main-content">
          <div class="video-player-container" id="videoContainer">
            <video 
              id="bhadkoVideoPlayer" 
              class="video-element" 
              playsinline 
              preload="metadata"
            ></video>

            <!-- Interactive TV News Studio Graphic & Chyron Overlay (Matching Screenshot Page 3) -->
            <div class="broadcast-stage-overlay" id="broadcastStageOverlay">
              <div class="stage-top-bar">
                <span class="live-indicator-tag">
                  <span class="pulse-dot"></span> ${this.t.liveBadge}
                </span>
                <span class="viewer-count-pill">
                  👁️ ${current.viewers || '12K'} ${this.t.watchingCount}
                </span>
              </div>

              ${current.category === 'News' ? `
                <div class="stage-news-ticker-wrapper">
                  <div class="ticker-breaking-badge">BREAKING NEWS</div>
                  <div class="ticker-headline-box">
                    ${current.ticker ? current.ticker.split('■')[0] : 'રાજ્યમાં આગામી 2 દિવસ ભારે વરસાદની આગાહી'}
                  </div>
                  <div class="ticker-sub-bar">
                    <div class="ticker-tag-region">ગુજરાત</div>
                    <div class="ticker-scroll-content">
                      ${current.ticker ? current.ticker : '■ વડોદરા, છોટાઉદેપુર, નર્મદા અને ડાંગ જિલ્લામાં ભારે વરસાદની શક્યતા'}
                    </div>
                    <div class="ticker-live-clock" id="broadcastLiveClock">08:42</div>
                  </div>
                </div>
              ` : ''}
            </div>

            <!-- Custom Floating Player Controls -->
            <div class="player-controls-bar">
              <div class="controls-left">
                <button class="player-btn" id="playerPlayBtn" title="Play/Pause">
                  ${ICONS.play}
                </button>
                <div class="volume-slider-group">
                  <button class="player-btn" id="playerMuteBtn" title="Mute/Unmute">
                    ${ICONS.volumeMute}
                  </button>
                  <input type="range" class="volume-slider" id="playerVolumeSlider" min="0" max="1" step="0.05" value="0.8" />
                </div>
              </div>

              <div class="controls-right">
                <select class="quality-select-dropdown" id="playerQualitySelect" title="Stream Quality">
                  <option value="-1">${this.t.qualityAuto}</option>
                  <option value="0">1080p Full HD</option>
                  <option value="1">720p HD</option>
                  <option value="2">480p SD</option>
                </select>
                <button class="player-btn" id="playerFullscreenBtn" title="Fullscreen">
                  ${ICONS.fullscreen}
                </button>
              </div>
            </div>

            <!-- Loading & Error Overlays -->
            <div class="player-state-overlay hidden" id="playerStateOverlay">
              <div class="player-spinner" id="playerSpinner"></div>
              <h3 id="playerStateTitle">${this.t.loadingStream}</h3>
              <p id="playerStateDesc" style="color: var(--text-secondary); font-size: 13px;"></p>
              <button class="btn btn-primary hidden" id="playerRetryBtn">
                ${ICONS.refresh}
                <span>${this.t.retryButton}</span>
              </button>
            </div>

            <!-- Click to Unmute Banner -->
            <button class="player-unmute-banner" id="unmuteBanner">
              ${ICONS.volumeHigh}
              <span>અવાજ શરૂ કરવા માટે ક્લિક કરો (Click to Unmute)</span>
            </button>
          </div>

          <!-- Channel Main Information Bar -->
          <div class="player-info-section">
            <div class="channel-main-header">
              <h1>
                <span>${current.name}</span>
                ${current.isVerified ? `<span class="verified-badge">${this.t.verified}</span>` : ''}
              </h1>
              <p class="channel-main-subtitle">
                ${current.about?.network || 'BHADKO DIGITAL TV MEDIA'} — ${current.gujaratiName || current.category}
              </p>
            </div>

            <div class="player-action-buttons">
              <button class="btn btn-secondary" id="btnShareChannel">
                ${ICONS.share}
                <span>${this.t.share}</span>
              </button>
              <button class="btn player-fav-btn ${isFav ? 'is-fav' : ''}" id="btnPlayerFavorite">
                ${isFav ? ICONS.starFilled : ICONS.starOutline}
                <span>${isFav ? this.t.removeFromFavorites : this.t.addToFavorites}</span>
              </button>
            </div>
          </div>

          <!-- Tabs Section: About | Program Schedule | Related Channels -->
          <div class="player-tabs-header">
            <button class="player-tab-btn ${this.activePlayerTab === 'about' ? 'active' : ''}" data-tab="about">
              ${this.t.tabAbout}
            </button>
            <button class="player-tab-btn ${this.activePlayerTab === 'schedule' ? 'active' : ''}" data-tab="schedule">
              ${this.t.tabSchedule}
            </button>
            <button class="player-tab-btn ${this.activePlayerTab === 'related' ? 'active' : ''}" data-tab="related">
              ${this.t.tabRelated}
            </button>
          </div>

          <div class="tab-pane" id="playerTabPane">
            ${this.renderPlayerTabContent(current)}
          </div>
        </section>
      </div>
    `;

    this.initPlayerEngine(current);
    this.bindPlayerEvents();
  }

  renderSidebarItems(filterQuery = '') {
    let list = this.channels;
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase().trim();
      list = list.filter(c => 
        c.name.toLowerCase().includes(q) || 
        (c.gujaratiName && c.gujaratiName.toLowerCase().includes(q)) ||
        c.category.toLowerCase().includes(q)
      );
    }

    return list.map(c => {
      const isActive = this.selectedChannel && this.selectedChannel.id === c.id;
      const isFav = StorageManager.isFavorite(c.id);
      const isLive = c.status === 'LIVE';

      return `
        <div class="sidebar-channel-item ${isActive ? 'active' : ''}" data-channel-id="${c.id}" tabindex="0">
          <div class="sidebar-item-logo">
            ${getChannelLogoSvg(c)}
          </div>
          <div class="sidebar-item-meta">
            <div class="sidebar-item-name">${c.name}</div>
            <div class="sidebar-item-sub">${c.gujaratiName || ''} (${c.category})</div>
          </div>
          <div class="sidebar-item-status">
            ${isLive ? `<span class="status-pill status-live" style="padding: 2px 5px; font-size: 9px;"><span class="pulse-dot"></span> LIVE</span>` : `<span class="status-pill status-offline" style="padding: 2px 5px; font-size: 9px;">OFF</span>`}
            <button class="sidebar-item-fav-btn ${isFav ? 'is-fav' : ''}" data-action="toggle-fav" data-id="${c.id}">
              ${isFav ? ICONS.starFilled : ICONS.starOutline}
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  renderPlayerTabContent(channel) {
    if (this.activePlayerTab === 'schedule') {
      const schedule = channel.schedule || [];
      return `
        <div class="epg-schedule-list">
          ${schedule.map(item => `
            <div class="epg-item ${item.isLive ? 'is-live' : ''}">
              <span class="epg-time">${item.time}</span>
              <div class="epg-title">
                ${item.title}
                ${item.isLive ? `<span class="status-pill status-live" style="margin-left: 8px; font-size: 9px;">${this.t.nowPlaying}</span>` : ''}
              </div>
              <span class="epg-host">${item.host || ''}</span>
            </div>
          `).join('')}
        </div>
      `;
    }

    if (this.activePlayerTab === 'related') {
      const related = this.channels.filter(c => c.category === channel.category && c.id !== channel.id);
      return `
        <div class="channels-grid" style="grid-template-columns: repeat(3, 1fr); gap: 16px;">
          ${related.slice(0, 3).map(c => this.renderChannelCard(c)).join('')}
        </div>
      `;
    }

    // Default: About Tab
    const about = channel.about || {};
    return `
      <div class="about-specs-grid">
        <div class="spec-card">
          <div class="spec-label">${this.t.studio}</div>
          <div class="spec-val">${about.broadcastStudio || 'Ahmedabad, Gujarat'}</div>
        </div>
        <div class="spec-card">
          <div class="spec-label">${this.t.network}</div>
          <div class="spec-val">${about.network || 'BHADKO DIGITAL TV MEDIA'}</div>
        </div>
        <div class="spec-card">
          <div class="spec-label">${this.t.resolution}</div>
          <div class="spec-val">${about.resolution || '1080p Full HD'}</div>
        </div>
        <div class="spec-card">
          <div class="spec-label">${this.t.bitrate}</div>
          <div class="spec-val">${about.bitrate || '4.8 Mbps'}</div>
        </div>
        <div class="spec-card">
          <div class="spec-label">${this.t.audio}</div>
          <div class="spec-val">${about.audio || 'Stereo 5.1'}</div>
        </div>
        <div class="spec-card">
          <div class="spec-label">${this.t.editorialChief}</div>
          <div class="spec-val">${about.editorialChief || 'Rajesh Trivedi'}</div>
        </div>
      </div>
    `;
  }

  initPlayerEngine(channel) {
    const video = document.getElementById('bhadkoVideoPlayer');
    const container = document.getElementById('videoContainer');
    const overlay = document.getElementById('playerStateOverlay');
    const stateTitle = document.getElementById('playerStateTitle');
    const stateDesc = document.getElementById('playerStateDesc');
    const retryBtn = document.getElementById('playerRetryBtn');
    const spinner = document.getElementById('playerSpinner');
    const playBtn = document.getElementById('playerPlayBtn');
    const muteBtn = document.getElementById('playerMuteBtn');
    const unmuteBanner = document.getElementById('unmuteBanner');

    if (!video || !container) return;

    if (this.playerEngine) {
      this.playerEngine.destroy();
    }

    this.playerEngine = new PlayerEngine(video, container, {
      onStateChange: (state, errorMsg) => {
        if (state === 'playing') {
          overlay?.classList.add('hidden');
          if (playBtn) playBtn.innerHTML = ICONS.pause;
        } else if (state === 'buffering' || state === 'loading') {
          overlay?.classList.remove('hidden');
          spinner?.classList.remove('hidden');
          retryBtn?.classList.add('hidden');
          if (stateTitle) stateTitle.textContent = this.t.loadingStream;
          if (stateDesc) stateDesc.textContent = errorMsg || '';
        } else if (state === 'error' || state === 'offline') {
          overlay?.classList.remove('hidden');
          spinner?.classList.add('hidden');
          retryBtn?.classList.remove('hidden');
          if (stateTitle) stateTitle.textContent = this.t.streamUnavailable;
          if (stateDesc) stateDesc.textContent = errorMsg || this.t.streamUnavailableDesc;
        }
      },
      onQualityLevels: (levels) => {
        const qualitySelect = document.getElementById('playerQualitySelect');
        if (qualitySelect && levels.length > 0) {
          qualitySelect.innerHTML = `<option value="-1">${this.t.qualityAuto}</option>` +
            levels.map((lvl, idx) => `<option value="${idx}">${lvl.height}p (${Math.round(lvl.bitrate / 1000)}k)</option>`).join('');
        }
      },
      onVolumeChange: (vol, isMuted) => {
        if (muteBtn) {
          muteBtn.innerHTML = (isMuted || vol === 0) ? ICONS.volumeMute : ICONS.volumeHigh;
        }
        if (unmuteBanner) {
          if (!isMuted) {
            unmuteBanner.classList.add('hidden');
          }
        }
      }
    });

    this.playerEngine.loadChannel(channel);
  }

  bindPlayerEvents() {
    // Sidebar channel item click
    const sidebarList = document.getElementById('sidebarChannelsList');
    if (sidebarList) {
      sidebarList.addEventListener('click', (e) => {
        const favBtn = e.target.closest('[data-action="toggle-fav"]');
        if (favBtn) {
          e.stopPropagation();
          const channelId = favBtn.getAttribute('data-id');
          this.toggleFavorite(channelId);
          return;
        }

        const item = e.target.closest('.sidebar-channel-item');
        if (item) {
          const channelId = item.getAttribute('data-channel-id');
          const channel = StorageManager.getChannelById(channelId);
          if (channel) {
            this.selectedChannel = channel;
            this.renderPlayerView(document.getElementById('mainContent'));
          }
        }
      });
    }

    // Sidebar search filter
    const sidebarSearch = document.getElementById('sidebarSearchInput');
    if (sidebarSearch) {
      sidebarSearch.addEventListener('input', (e) => {
        if (sidebarList) {
          sidebarList.innerHTML = this.renderSidebarItems(e.target.value);
        }
      });
    }

    // Player Play/Pause
    const playBtn = document.getElementById('playerPlayBtn');
    if (playBtn) {
      playBtn.addEventListener('click', () => {
        if (this.playerEngine) {
          this.playerEngine.togglePlay();
          playBtn.innerHTML = this.playerEngine.video.paused ? ICONS.play : ICONS.pause;
        }
      });
    }

    // Player Mute Toggle
    const muteBtn = document.getElementById('playerMuteBtn');
    if (muteBtn) {
      muteBtn.addEventListener('click', () => {
        if (this.playerEngine) {
          this.playerEngine.toggleMute();
        }
      });
    }

    // Unmute Banner
    const unmuteBanner = document.getElementById('unmuteBanner');
    if (unmuteBanner) {
      unmuteBanner.addEventListener('click', () => {
        if (this.playerEngine) {
          this.playerEngine.video.muted = false;
          unmuteBanner.classList.add('hidden');
        }
      });
    }

    // Volume Slider
    const volumeSlider = document.getElementById('playerVolumeSlider');
    if (volumeSlider) {
      volumeSlider.addEventListener('input', (e) => {
        if (this.playerEngine) {
          this.playerEngine.setVolume(parseFloat(e.target.value));
        }
      });
    }

    // Fullscreen
    const fullscreenBtn = document.getElementById('playerFullscreenBtn');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => {
        if (this.playerEngine) {
          this.playerEngine.toggleFullscreen();
        }
      });
    }

    // Quality Selector
    const qualitySelect = document.getElementById('playerQualitySelect');
    if (qualitySelect) {
      qualitySelect.addEventListener('change', (e) => {
        if (this.playerEngine) {
          this.playerEngine.setQuality(parseInt(e.target.value));
        }
      });
    }

    // Retry Button
    const retryBtn = document.getElementById('playerRetryBtn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        if (this.playerEngine) {
          this.playerEngine.retry();
        }
      });
    }

    // Share Button
    const shareBtn = document.getElementById('btnShareChannel');
    if (shareBtn) {
      shareBtn.addEventListener('click', () => {
        const url = window.location.href;
        navigator.clipboard?.writeText(url).then(() => {
          this.showToast(this.t.shareSuccess);
        }).catch(() => {
          this.showToast(this.t.shareSuccess);
        });
      });
    }

    // Favorite Button in Player
    const playerFavBtn = document.getElementById('btnPlayerFavorite');
    if (playerFavBtn && this.selectedChannel) {
      playerFavBtn.addEventListener('click', () => {
        const isFav = this.toggleFavorite(this.selectedChannel.id);
        playerFavBtn.classList.toggle('is-fav', isFav);
        playerFavBtn.querySelector('span').textContent = isFav ? this.t.removeFromFavorites : this.t.addToFavorites;
      });
    }

    // Tabs
    document.querySelectorAll('.player-tab-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.activePlayerTab = e.currentTarget.getAttribute('data-tab');
        document.querySelectorAll('.player-tab-btn').forEach(b => b.classList.remove('active'));
        e.currentTarget.classList.add('active');
        const pane = document.getElementById('playerTabPane');
        if (pane && this.selectedChannel) {
          pane.innerHTML = this.renderPlayerTabContent(this.selectedChannel);
        }
      });
    });
  }

  // =========================================================
  // VIEW: FAVORITES
  // =========================================================
  renderFavoritesView(container) {
    const favIds = StorageManager.getFavorites();
    const favChannels = this.channels.filter(c => favIds.includes(c.id));

    container.innerHTML = `
      <section class="discovery-view">
        <div class="discovery-header">
          <div class="discovery-title-group">
            <h1>${this.t.favoritesTitle}</h1>
            <p>${this.t.favoritesSubtitle}</p>
          </div>
        </div>

        <div class="channels-grid">
          ${favChannels.length > 0 ? favChannels.map(c => this.renderChannelCard(c)).join('') : `
            <div class="empty-channels-state">
              <div class="empty-state-icon" style="color: var(--bhadko-amber);">
                ${ICONS.starFilled}
              </div>
              <h3>${this.t.noFavoritesTitle}</h3>
              <p>${this.t.noFavoritesDesc}</p>
              <button class="btn btn-primary" id="btnExploreFromFav">${this.t.browseChannelsBtn}</button>
            </div>
          `}
        </div>
      </section>
    `;

    const exploreBtn = document.getElementById('btnExploreFromFav');
    if (exploreBtn) {
      exploreBtn.addEventListener('click', () => this.switchView('channels'));
    }

    // Bind card clicks
    container.querySelectorAll('.channel-card').forEach(card => {
      card.addEventListener('click', (e) => {
        const favBtn = e.target.closest('[data-action="toggle-fav"]');
        if (favBtn) {
          e.stopPropagation();
          const channelId = favBtn.getAttribute('data-id');
          this.toggleFavorite(channelId);
          this.renderFavoritesView(container);
          return;
        }

        const channelId = card.getAttribute('data-channel-id');
        const channel = StorageManager.getChannelById(channelId);
        if (channel) {
          this.selectedChannel = channel;
          this.switchView('player');
        }
      });
    });
  }

  // =========================================================
  // VIEW: CATEGORIES
  // =========================================================
  renderCategoriesView(container) {
    container.innerHTML = `
      <section class="discovery-view">
        <div class="discovery-header">
          <div class="discovery-title-group">
            <h1>${this.t.navCategories}</h1>
            <p>ચેનલોને તેમની શ્રેણી પ્રમાણે ફિલ્ટર કરો અને લાઇવ જુઓ.</p>
          </div>
        </div>

        <div class="channels-grid" style="grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));">
          ${CATEGORIES.filter(c => c.id !== 'all').map(cat => {
            const count = this.channels.filter(ch => ch.category === cat.id).length;
            const label = this.currentLang === 'hi' ? cat.labelHi : (this.currentLang === 'en' ? cat.labelEn : cat.labelGu);
            return `
              <div class="spec-card category-card-box" data-cat="${cat.id}" style="cursor: pointer; padding: 24px; border: 1px solid var(--border-medium); border-radius: var(--radius-lg); transition: all 0.2s;" tabindex="0">
                <div style="font-size: 28px; margin-bottom: 12px; color: var(--bhadko-orange);">📺</div>
                <h3 style="font-size: 20px; font-weight: 800; margin-bottom: 6px;">${label}</h3>
                <p style="color: var(--text-secondary); font-size: 14px;">${count} ચેનલો ઉપલબ્ધ</p>
              </div>
            `;
          }).join('')}
        </div>
      </section>
    `;

    container.querySelectorAll('.category-card-box').forEach(card => {
      card.addEventListener('click', (e) => {
        const cat = e.currentTarget.getAttribute('data-cat');
        this.selectedCategory = cat;
        this.switchView('channels');
      });
    });
  }

  // =========================================================
  // VIEW: ADMIN MANAGEMENT CONSOLE
  // =========================================================
  renderAdminView(container = null) {
    const target = container || document.getElementById('mainContent');
    if (!target) return;

    target.innerHTML = this.adminController.renderAdminView(this.currentLang, TRANSLATIONS);
    this.bindAdminEvents();
  }

  bindAdminEvents() {
    const addBtn = document.getElementById('btnAdminAddChannel');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        this.openChannelEditorModal(null);
      });
    }

    const table = document.querySelector('.admin-channels-table');
    if (table) {
      table.addEventListener('click', (e) => {
        const editBtn = e.target.closest('[data-action="edit"]');
        if (editBtn) {
          const id = editBtn.getAttribute('data-id');
          const channel = StorageManager.getChannelById(id);
          this.openChannelEditorModal(channel);
          return;
        }

        const deleteBtn = e.target.closest('[data-action="delete"]');
        if (deleteBtn) {
          const id = deleteBtn.getAttribute('data-id');
          const channel = StorageManager.getChannelById(id);
          this.openDeleteConfirmModal(channel);
          return;
        }

        const previewBtn = e.target.closest('[data-action="preview"]');
        if (previewBtn) {
          const id = previewBtn.getAttribute('data-id');
          const channel = StorageManager.getChannelById(id);
          if (channel) {
            this.selectedChannel = channel;
            this.switchView('player');
          }
          return;
        }

        const statusBtn = e.target.closest('[data-action="toggle-status"]');
        if (statusBtn) {
          const id = statusBtn.getAttribute('data-id');
          const channel = StorageManager.getChannelById(id);
          if (channel) {
            channel.status = channel.status === 'LIVE' ? 'OFFLINE' : 'LIVE';
            StorageManager.saveChannel(channel);
            this.channels = StorageManager.getChannels();
            this.renderAdminView();
            this.showToast(`ચેનલ સ્થિતિ અપડેટ થઈ: ${channel.status}`);
          }
        }
      });
    }
  }

  openChannelEditorModal(channel) {
    const modalContainer = document.getElementById('modalContainer');
    modalContainer.innerHTML = this.adminController.renderEditModal(channel, this.currentLang, TRANSLATIONS);

    const closeBtn = document.getElementById('modalCloseBtn');
    const cancelBtn = document.getElementById('btnCancelEdit');
    const form = document.getElementById('channelForm');

    const closeModal = () => { modalContainer.innerHTML = ''; };

    closeBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const id = document.getElementById('editChannelId').value;
      const channelData = {
        id: id || `channel-${Date.now()}`,
        name: document.getElementById('inputChannelName').value.trim(),
        gujaratiName: document.getElementById('inputGujaratiName').value.trim(),
        category: document.getElementById('inputCategory').value,
        language: document.getElementById('inputLanguage').value,
        status: document.getElementById('inputStatus').value,
        streamUrl: document.getElementById('inputStreamUrl').value.trim(),
        description: document.getElementById('inputDescription').value.trim(),
        logoColor: document.getElementById('inputLogoColor').value,
        displayOrder: parseInt(document.getElementById('inputDisplayOrder').value) || 1,
        viewers: channel?.viewers || '10.2K',
        isVerified: true
      };

      StorageManager.saveChannel(channelData);
      this.channels = StorageManager.getChannels();
      closeModal();
      this.renderAdminView();
      this.showToast(this.t.saveSuccessMsg);
    });
  }

  openDeleteConfirmModal(channel) {
    const modalContainer = document.getElementById('modalContainer');
    modalContainer.innerHTML = this.adminController.renderDeleteConfirmModal(channel, this.currentLang, TRANSLATIONS);

    const closeBtn = document.getElementById('modalCloseDeleteBtn');
    const cancelBtn = document.getElementById('btnCancelDelete');
    const confirmBtn = document.getElementById('btnConfirmDeleteFinal');

    const closeModal = () => { modalContainer.innerHTML = ''; };

    closeBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);

    confirmBtn?.addEventListener('click', () => {
      StorageManager.deleteChannel(channel.id);
      this.channels = StorageManager.getChannels();
      closeModal();
      this.renderAdminView();
      this.showToast(this.t.deleteSuccessMsg);
    });
  }

  // =========================================================
  // GLOBAL INTERACTIONS & HELPERS
  // =========================================================
  toggleFavorite(channelId) {
    const isNowFav = StorageManager.toggleFavorite(channelId);
    
    // Update badge in nav
    const favCount = document.getElementById('navFavCount');
    if (favCount) {
      favCount.textContent = StorageManager.getFavorites().length;
    }

    // Update icons in current view
    document.querySelectorAll(`[data-action="toggle-fav"][data-id="${channelId}"]`).forEach(btn => {
      btn.classList.toggle('is-fav', isNowFav);
      btn.innerHTML = isNowFav ? ICONS.starFilled : ICONS.starOutline;
    });

    this.showToast(isNowFav ? this.t.addToFavorites : this.t.removeFromFavorites);
    return isNowFav;
  }

  showToast(message) {
    const container = document.getElementById('toastContainer');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML = `
      ${ICONS.check}
      <span>${message}</span>
    `;

    container.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(10px)';
      toast.style.transition = 'all 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 2800);
  }

  bindGlobalEvents() {
    // Brand logo returns to home
    document.getElementById('brandLogoBtn')?.addEventListener('click', () => {
      this.switchView('channels');
    });

    // Navigation links
    document.querySelectorAll('[data-nav]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const view = e.currentTarget.getAttribute('data-nav');
        this.switchView(view);
      });
    });

    // Global Search (< 1s live search)
    const searchInput = document.getElementById('globalSearchInput');
    const clearBtn = document.getElementById('searchClearBtn');

    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.searchQuery = e.target.value;
        if (clearBtn) {
          clearBtn.classList.toggle('hidden', !this.searchQuery);
        }

        if (this.currentView !== 'channels') {
          this.switchView('channels');
        } else {
          this.renderChannelsView(document.getElementById('mainContent'));
        }
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.searchQuery = '';
        if (searchInput) searchInput.value = '';
        clearBtn.classList.add('hidden');
        if (this.currentView === 'channels') {
          this.renderChannelsView(document.getElementById('mainContent'));
        }
      });
    }

    // Mobile Search Tab
    document.getElementById('mobileSearchTab')?.addEventListener('click', () => {
      this.switchView('channels');
      setTimeout(() => {
        const input = document.getElementById('globalSearchInput');
        input?.focus();
      }, 100);
    });

    // Language Dropdown
    const langBtn = document.getElementById('langSelectBtn');
    const langMenu = document.getElementById('langMenuDropdown');

    langBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      langMenu?.classList.toggle('hidden');
    });

    document.addEventListener('click', () => {
      langMenu?.classList.add('hidden');
    });

    document.querySelectorAll('.lang-option-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const lang = e.currentTarget.getAttribute('data-lang');
        this.currentLang = lang;
        StorageManager.setLanguage(lang);
        this.renderShell();
        this.initTVNavigation();
        this.initAdmin();
        this.renderCurrentView();
        this.bindGlobalEvents();
      });
    });

    // TV Mode Toggle
    document.getElementById('btnToggleTvMode')?.addEventListener('click', () => {
      const isEnabled = this.tvNavigator.toggle();
      StorageManager.setTVMode(isEnabled);
    });

    // Admin Console Button
    document.getElementById('btnOpenAdmin')?.addEventListener('click', () => {
      this.switchView('admin');
    });
  }
}

// Start application once DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.bhadkoApp = new BhadkoApp();
});
